import { useTheme, createTheme } from '@mui/material/styles'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSectionMetaData } from '@v4/components/common/SectionItem/SectionItem.context'
import PageTemplateData from '@v4/utils/data/PageTemplateData'
import AppStore from '@v4/store/appStore'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import { deepmerge } from '@mui/utils'
import useStyles from './PDFContainer.styled'
import { BORDER_WIDTH } from './PDFContainer.const'

export const usePDFContainer = ({ children, collection, active }) => {
	const metaData = useSectionMetaData()
	const classes = useStyles({ active })
	const ref = useRef()
	const [visible, setVisible] = useState(false)
	const isLargePage = PageBuilderSelector.useIsLargePage()
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const withFallback =
		isLargePage &&
		!import.meta.env.SSR &&
		isBuilderMode &&
		collection === PageTemplateData.SORT_GROUPS.MAIN &&
		!active
	const { showBorders } = useTheme()
	const contentTheme = useCallback(parentTheme => {
		if (!parentTheme.showBorders) return parentTheme

		return createTheme(
			deepmerge(parentTheme, {
				margins: {
					top: BORDER_WIDTH,
					left: BORDER_WIDTH,
					bottom: BORDER_WIDTH,
					right: BORDER_WIDTH,
				},
				components: {
					MuiContainer: {
						styleOverrides: {
							root: {
								paddingLeft: parentTheme.spacing(BORDER_WIDTH),
								paddingRight: parentTheme.spacing(BORDER_WIDTH),
								[parentTheme.breakpoints.up('sm')]: {
									paddingLeft: parentTheme.spacing(BORDER_WIDTH),
									paddingRight: parentTheme.spacing(BORDER_WIDTH),
								},
							},
						},
					},
				},
			}),
		)
	}, [])

	useEffect(() => {
		if (!withFallback || !ref.current) {
			return undefined
		}
		const io = new IntersectionObserver(
			entries => {
				setVisible(entries[0].isIntersecting)
			},
			{
				rootMargin: '2000px',
			},
		)
		io.observe(ref.current)
		return () => io.disconnect()
	}, [visible, withFallback])

	return [
		{
			ref,
			classes,
			collection,
			showBorders,
			contentTheme,
			children,
			withBgImage: !!metaData.backgroundImage?.image,
			bgImage: metaData.backgroundImage,
			bgShading: metaData.backgroundShading?.toNumber(),
			visible: withFallback ? visible : true,
		},
	]
}
